import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, FrownIcon, LaughIcon, SmileIcon, XIcon } from 'lucide-react';
const ease = [0.23, 1, 0.32, 1] as const;
type Rating = 'pas-du-tout' | 'oui' | 'parfaitement';
const ratings: {
  id: Rating;
  label: string;
  thanks: string;
  Icon: typeof SmileIcon;
}[] = [{
  id: 'pas-du-tout',
  label: 'Pas du tout',
  thanks: 'Nous allons nous en servir pour améliorer ATNA.',
  Icon: FrownIcon
}, {
  id: 'oui',
  label: 'Oui',
  thanks: 'Votre avis nous aide à améliorer les services de la Ville.',
  Icon: SmileIcon
}, {
  id: 'parfaitement',
  label: 'Parfaitement',
  thanks: 'Ravie d’avoir pu vous aider !',
  Icon: LaughIcon
}];

/** Mini sondage affiché à la fin d'un échange avec ATNA : trois choix, un merci, rien de plus. */
export function AtnaSatisfaction() {
  const [rating, setRating] = useState<Rating | null>(null);
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  const chosen = ratings.find(item => item.id === rating);
  return <motion.li initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.24,
    ease,
    delay: 0.25
  }} className="sm:pl-12" data-id="element-305" data-mp="1j8au2rv7x2vx">
      <section aria-labelledby="atna-nps-titre" className="rounded-3xl border border-dashed border-slate-300 bg-ville-mist/40 px-5 py-4" data-id="element-306" data-mp="19gwoc2cob6uc">
        {!chosen ? <>
            <div className="flex items-start justify-between gap-3" data-id="element-307" data-mp="226n56j5owmu5">
              <p id="atna-nps-titre" className="text-sm font-bold text-ville-navy" data-id="element-308" data-mp="1x7af7pjiuznc">
                ATNA a-t-elle répondu à votre besoin ?
              </p>
              <button type="button" onClick={() => setDismissed(true)} aria-label="Ne pas répondre au sondage" className="-mr-2 -mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-full text-slate-400 transition-colors duration-150 ease-ville hover:bg-white hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-309" data-mp="1s6mhkl0sfkk5">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-310" data-mp="341hpki5am3wy" />
              </button>
            </div>
            <div role="group" aria-labelledby="atna-nps-titre" className="mt-2 flex items-start justify-center gap-6" data-id="element-311" data-mp="2q1p1yvp2c3t7">
              {ratings.map(({
            id,
            label,
            Icon
          }) => <button key={id} type="button" onClick={() => setRating(id)} className="group flex w-20 flex-col items-center gap-1 rounded-2xl py-1.5 text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-312" data-mp="2v2wc31eohf31">
                  <Icon className="h-9 w-9 transition-transform duration-150 ease-ville group-hover:scale-110 group-active:scale-95" strokeWidth={1.6} aria-hidden="true" data-id="element-313" data-mp="2cak2btlp3biv" />
                  <span className="text-xs font-semibold" data-id="element-314" data-mp="1ipki3dh9ale8">{label}</span>
                </button>)}
            </div>
          </> : <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2,
        ease
      }} className="flex items-center gap-3" role="status" data-id="element-315" data-mp="1eqtwo3m5ks6y">
            <chosen.Icon className="h-9 w-9 shrink-0 text-ville-indigo" strokeWidth={1.6} aria-label={chosen.label} data-id="element-316" data-mp="21o56uyvodeop" />
            <div data-id="element-317" data-mp="1xn3hd9rk8gzx">
              <p id="atna-nps-titre" className="flex items-center gap-1.5 text-sm font-bold text-ville-navy" data-id="element-318" data-mp="1xlimfj1c2w1j">
                <CheckIcon className="h-4 w-4 text-ville-indigo" aria-hidden="true" data-id="element-319" data-mp="2bgugrpbkukhx" />
                Merci pour votre avis !
              </p>
              <p className="text-xs text-slate-500" data-id="element-320" data-mp="2lcecaam8bh65">{chosen.thanks}</p>
            </div>
          </motion.div>}
      </section>
    </motion.li>;
}